import React, { useMemo, useEffect, useState } from 'react';
import { Music, User, XCircle, Edit, Play, Pause, AlertTriangle } from 'lucide-react';
import { formatCurrency, getAgeDivision } from '../../utils/competitionUtils';
import { SelectedRoutineCardProps } from '../../types/competition';
import { Routine } from '../../types/registration';

/**
 * Card component for a selected routine with style selection
 */
const SelectedRoutineCard: React.FC<SelectedRoutineCardProps> = ({
  routine,
  routineStyles,
  competitionCategories = [],
  updateRoutineOptions,
  toggleRoutineSelection,
  handleEditRoutine,
  calculateRoutinePrice,
  toggleAudio,
  currentlyPlaying,
  isDarkMode
}) => {
  // Derive needed values
  const dancerCount = Array.isArray(routine.dancers) ? routine.dancers.length : 0;
  
  // Determine the correct ID to use (prefer routineCloneId)
  const routineCloneId = routine.routineCloneId;
  const originalId = routine.originalRoutineId || routine.routineId;
  const routineId = routineCloneId || routine.routineId || '';
  
  // Log IDs for debugging
  console.log(`SelectedRoutineCard using ID: ${routineId} (cloneId: ${routineCloneId}, originalId: ${originalId}, routineId: ${routine.routineId})`);
  
  // Local state to track style value - initialize with routine.style
  const [localStyle, setLocalStyle] = useState(routine.style || '');
  
  // Update local state when routine.style changes from parent
  useEffect(() => {
    if (routine.style && routine.style !== localStyle) {
      console.log(`Updating local style state from ${localStyle} to ${routine.style}`);
      setLocalStyle(routine.style);
    }
  }, [routine.style, localStyle]);
  
  // Calculate if style is selected based on the local state
  const isStyleSelected = !!localStyle;
  
  // Placeholder for age divisions - this would normally come from props
  const ageDivisions: Array<{
    label: string;
    minAge?: number;
    maxAge?: number;
    isCompetitive?: boolean;
  }> = [];
  
  // Check if the routine age group is competitive
  const ageGroupInfo = useMemo(() => {
    // Skip if no age divisions, no dancers, or no average age
    if (!ageDivisions || !ageDivisions.length || !routine.averageAge) {
      return { isCompetitive: true, name: null };
    }
    
    // Get only competitive age divisions for this check
    const competitiveAgeGroups = ageDivisions.filter(div => div.isCompetitive === true);
    
    // Log all age groups for debugging
    console.log(`Age groups for routine ${routine.title || 'Untitled'}:`, 
      ageDivisions.map((g: any) => ({
        label: g.label, 
        isCompetitive: g.isCompetitive === true,
        min: g.minAge,
        max: g.maxAge
      }))
    );
    
    // First check: If routine already has an ageGroup assigned, find that exact group
    if (routine.ageGroup) {
      // Find by name/label
      const matchingGroup = ageDivisions.find((group: any) => group.label === routine.ageGroup);
      
      if (matchingGroup) {
        console.log(`Found existing age group "${matchingGroup.label}" with isCompetitive=${matchingGroup.isCompetitive === true}`);
        return {
          isCompetitive: matchingGroup.isCompetitive === true,
          name: matchingGroup.label
        };
      }
    }
      
    // Second check: Use the getAgeDivision utility to determine age group from averageAge
    // This function ONLY considers competitive age groups by design
    const competitiveAgeGroupName = getAgeDivision(routine.averageAge, ageDivisions);
    if (competitiveAgeGroupName && competitiveAgeGroupName !== 'Unknown') {
      console.log(`Determined competitive age group: ${competitiveAgeGroupName}`);
      return {
        isCompetitive: true, // Must be competitive since getAgeDivision only returns competitive groups
        name: competitiveAgeGroupName
      };
    }
    
    // If we got here, look for any existing age group even if non-competitive
    // This helps show a warning if a non-competitive group is already assigned
    if (routine.ageGroup) {
      console.log(`Using existing non-competitive age group: ${routine.ageGroup}`);
      return {
        isCompetitive: false,
        name: routine.ageGroup
      };
    }
    
    // Default fallback
    return {
      isCompetitive: true,
      name: null
    };
  }, [routine.averageAge, routine.ageGroup, ageDivisions]);
  
  // Update the routine's age group when it changes from calculation
  useEffect(() => {
    // Only update if we have a valid age group from calculation and it's different from the current one
    if (ageGroupInfo.name && 
        ageGroupInfo.isCompetitive && 
        (!routine.ageGroup || routine.ageGroup !== ageGroupInfo.name)) {
      console.log(`Updating routine ${routineId} age group to: ${ageGroupInfo.name}`);
      
      try {
        updateRoutineOptions(routineId, { ageGroup: ageGroupInfo.name });
      } catch (error) {
        console.error("Error updating age group:", error);
      }
    }
  }, [ageGroupInfo.name, ageGroupInfo.isCompetitive, routine.ageGroup, routineId, updateRoutineOptions]);
  
  // Handler for style change
  const handleStyleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStyle = e.target.value;
    console.log(`Setting style for routine ${routineId} to: ${newStyle}`);
    
    // Update local state for immediate UI feedback
    setLocalStyle(newStyle);
    
    try {
      updateRoutineOptions(routineId, { style: newStyle });
    } catch (error) {
      console.error("Error updating style:", error);
    }
  };

  // Handler for category change
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCategory = e.target.value;
    console.log(`Setting category for routine ${routineId} to: ${newCategory}`);
    
    try {
      updateRoutineOptions(routineId, { category: newCategory });
    } catch (error) {
      console.error("Error updating category:", error);
    }
  };

  // Handler for removal
  const handleRemove = () => {
    toggleRoutineSelection(routine);
  };

  // Handler for audio toggle
  const handleAudioToggle = () => {
    // Pass the entire routine object, not just the ID
    // Also add null check to prevent errors
    if (routine.songFileUrl) {
      toggleAudio(routine.songFileUrl);
    }
  };

  // Handler for edit
  const handleEdit = () => {
    handleEditRoutine(routine);
  };

  // Format routine price
  const routinePrice = calculateRoutinePrice(routine);
  const formattedPrice = formatCurrency(routinePrice);

  // Determine if this routine's audio is currently playing
  const isPlaying = currentlyPlaying === routine.songFileUrl;
  const hasSong = Boolean(routine.songFileUrl);

  return (
    <div
      className={`border rounded-lg p-3 ${
        isDarkMode
          ? "border-blue-500/30 bg-blue-900/20"
          : "border-blue-200 bg-blue-50"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
              isDarkMode
                ? "bg-blue-800/30 text-blue-400"
                : "bg-blue-100 text-blue-600"
            }`}
          >
            <Music className="w-4 h-4" />
          </div>
          <h3
            className={`font-medium ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {routine.title || routine.name || 'Untitled Routine'}
          </h3>
        </div>

        <div className="flex items-center space-x-1">
          {/* Play Audio Button */}
          {hasSong && (
            <button
              onClick={handleAudioToggle}
              className={`p-1 rounded-full transition-all ${
                isPlaying
                  ? isDarkMode
                    ? "bg-blue-500/20 text-blue-400"
                    : "bg-blue-100 text-blue-600"
                  : isDarkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
              }`}
              aria-label={isPlaying ? 'Pause song' : 'Play song'}
              title={isPlaying ? 'Pause song' : 'Play song'}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </button>
          )}

          {/* Edit Button */}
          <button
            onClick={handleEdit}
            className={`p-1 rounded-full transition-all ${
              isDarkMode
                ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
            }`}
            aria-label="Edit routine"
            title="Edit routine"
          >
            <Edit size={16} />
          </button>

          {/* Remove Button */}
          <button
            onClick={handleRemove}
            className={`p-1 rounded-full transition-all ${
              isDarkMode
                ? "bg-red-900/20 text-red-400 hover:bg-red-900/30"
                : "bg-red-100 text-red-500 hover:bg-red-200"
            }`}
            aria-label="Remove routine"
            title="Remove routine"
          >
            <XCircle size={16} />
          </button>
        </div>
      </div>

      {/* Details */}
      <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 gap-x-2 mb-3">
        <span className="flex items-center">
          <User size={10} className="mr-1" />
          {dancerCount} {dancerCount === 1 ? 'dancer' : 'dancers'}
        </span>
        <span>Type: {routine.typeLabel || routine.type || 'Unknown'}</span>
        {routine.averageAge && <span>Avg. Age: {routine.averageAge}</span>}
        {ageGroupInfo.name && (
          <span className="flex items-center">
            Age Group: {ageGroupInfo.name}
            {!ageGroupInfo.isCompetitive && (
              <AlertTriangle size={10} className="ml-1 text-yellow-500" aria-label="Non-competitive age group" />
            )}
          </span>
        )}
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
        {/* Style Selection */}
        <div>
          <label
            htmlFor={`style-${routineId}`}
            className={`block text-xs font-medium mb-1 ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Style *
          </label>
          <select
            id={`style-${routineId}`}
            value={localStyle}
            onChange={handleStyleChange}
            className={`block w-full rounded-md text-sm ${
              !isStyleSelected
                ? isDarkMode
                  ? "border-red-500/50 bg-red-900/20 text-red-200"
                  : "border-red-300 bg-red-50 text-red-900"
                : isDarkMode
                  ? "border-gray-600 bg-gray-700 text-white"
                  : "border-gray-300 bg-white text-gray-900"
            }`}
            required
          >
            <option value="">Select Style</option>
            {routineStyles.map((style) => (
              <option key={style.value} value={style.value}>
                {style.label}
              </option>
            ))}
          </select>
        </div>

        {/* Category Selection */}
        <div>
          <label
            htmlFor={`category-${routineId}`}
            className={`block text-xs font-medium mb-1 ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Category *
          </label>
          <select
            id={`category-${routineId}`}
            value={routine.category || ''}
            onChange={handleCategoryChange}
            className={`block w-full rounded-md text-sm ${
              !routine.category
                ? isDarkMode
                  ? "border-red-500/50 bg-red-900/20 text-red-200"
                  : "border-red-300 bg-red-50 text-red-900"
                : isDarkMode
                  ? "border-gray-600 bg-gray-700 text-white"
                  : "border-gray-300 bg-white text-gray-900"
            }`}
            required
          >
            <option value="">Select Category</option>
            {competitionCategories.map((category) => (
              <option key={category.value} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Price */}
      <div
        className={`text-right text-sm font-medium ${
          isDarkMode ? "text-blue-400" : "text-blue-600"
        }`}
      >
        {formattedPrice}
      </div>
    </div>
  );
};

export default SelectedRoutineCard; 